import {
    Checkbox,
    Collapse,
    FormControlLabel,
    Grid,
    IconButton,
    Paper,
    Radio,
    RadioGroup,
    Tooltip,
    Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import AcxMainTextField from "components/UI/AcxMainTextField";
import type {
    KboToolConfiguration,
    ToolDefinition,
} from "components/KBo/Models/KboSettings";
import { KBO_TOOL_LOADING_MODES } from "components/KBo/Models/KboSettings";
import React, { useCallback, useMemo, useState } from "react";

const noRequiredSkills: string[] = [];

interface Props {
    toolConfigurations: KboToolConfiguration[];
    availableTools: ToolDefinition[];
    skillRequiredToolNames?: Record<string, string[]>;
    onChange: (toolConfigurations: KboToolConfiguration[]) => void;
    namespaceDescriptions: Record<string, string>;
    onNamespaceDescriptionsChange: (
        descriptions: Record<string, string>,
    ) => void;
}

export function KBoToolToggles({
    toolConfigurations,
    availableTools,
    skillRequiredToolNames,
    onChange,
    namespaceDescriptions,
    onNamespaceDescriptionsChange,
}: Props) {
    const namespaces = [
        ...new Set(
            toolConfigurations
                .filter((tool) => tool.enabled)
                .map((tool) => tool.namespace)
                .filter((name): name is string => Boolean(name)),
        ),
    ].sort();
    const configMap = useMemo(() => {
        const map = new Map<string, KboToolConfiguration>();
        for (const config of toolConfigurations) {
            map.set(config.functionName.toLocaleLowerCase(), config);
        }
        return map;
    }, [toolConfigurations]);

    const getConfig = useCallback(
        (tool: ToolDefinition): KboToolConfiguration => {
            return (
                configMap.get(tool.functionName.toLocaleLowerCase()) ?? {
                    functionName: tool.functionName,
                    namespace: tool.namespace,
                    enabled: false,
                    groupName: null,
                    loadingMode:
                        tool.defaultLoadingMode ??
                        KBO_TOOL_LOADING_MODES.deferred,
                }
            );
        },
        [configMap],
    );

    const updateTool = useCallback(
        (functionName: string, patch: Partial<KboToolConfiguration>) => {
            const existing = configMap.get(functionName.toLocaleLowerCase());
            const tool = availableTools.find(
                (candidate) =>
                    candidate.functionName.toLocaleLowerCase() ===
                    functionName.toLocaleLowerCase(),
            );
            const base: KboToolConfiguration = existing ?? {
                functionName,
                namespace: tool?.namespace,
                enabled: false,
                groupName: null,
                loadingMode:
                    tool?.defaultLoadingMode ?? KBO_TOOL_LOADING_MODES.deferred,
            };
            const updated = { ...base, ...patch };

            const newConfigs = existing
                ? toolConfigurations.map((c) =>
                      c.functionName.toLocaleLowerCase() ===
                      functionName.toLocaleLowerCase()
                          ? updated
                          : c,
                  )
                : [...toolConfigurations, updated];

            onChange(newConfigs);
        },
        [availableTools, configMap, toolConfigurations, onChange],
    );

    const getRequiredSkillNames = useCallback(
        (functionName: string) => {
            const exactMatch = skillRequiredToolNames?.[functionName];
            if (exactMatch) return exactMatch;

            const matchedKey = Object.keys(skillRequiredToolNames ?? {}).find(
                (key) =>
                    key.toLocaleLowerCase() ===
                    functionName.toLocaleLowerCase(),
            );
            return matchedKey
                ? skillRequiredToolNames?.[matchedKey] ?? noRequiredSkills
                : noRequiredSkills;
        },
        [skillRequiredToolNames],
    );

    const enabledCount = useMemo(
        () =>
            availableTools.filter((tool) => {
                const config = getConfig(tool);
                return (
                    config.enabled ||
                    getRequiredSkillNames(tool.functionName).length > 0
                );
            }).length,
        [availableTools, getConfig, getRequiredSkillNames],
    );

    return (
        // Opt the tool list out of scroll-anchoring so toggling a row never
        // nudges the surrounding scroll position (dialog or admin page).
        <Grid item sx={{ overflowAnchor: "none" }}>
            <Paper>
                <Grid container direction="column" rowGap={2} padding={3}>
                    <Grid item>
                        <Typography variant="h6">Tool Configuration</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Opt tools in for this organization. Only enabled
                            tools will be available to KBo. Tools in the same
                            namespace are grouped into the same hosted tool
                            search. Eager tools are exposed directly and
                            deferred tools are discovered on demand.
                        </Typography>
                        {availableTools.length > 0 && (
                            <Typography
                                variant="caption"
                                color="text.secondary"
                            >
                                {enabledCount} of {availableTools.length} tools
                                enabled
                            </Typography>
                        )}
                    </Grid>
                    {namespaces.map((namespace) => (
                            <Grid item key={namespace}>
                                <AcxMainTextField
                                    id={`namespace-description-${namespace}`}
                                    fullWidth
                                    multiline
                                    required
                                    labelText={`${namespace} namespace description`}
                                    value={
                                        namespaceDescriptions[namespace] ?? ""
                                    }
                                    error={
                                        !namespaceDescriptions[
                                            namespace
                                        ]?.trim()
                                    }
                                    helperText="Sent to the model for this namespace. Required when its tools are enabled."
                                    onBlur={(value) => {
                                        const description = String(value ?? "");
                                        if (
                                            description ===
                                            (namespaceDescriptions[namespace] ??
                                                "")
                                        )
                                            return;
                                        onNamespaceDescriptionsChange({
                                            ...namespaceDescriptions,
                                            [namespace]: description,
                                        });
                                    }}
                                />
                            </Grid>
                        ))}
                    {availableTools.map((tool) => {
                        const config = getConfig(tool);
                        return (
                            <ToolRow
                                key={tool.functionName}
                                tool={tool}
                                config={config}
                                requiredBySkills={getRequiredSkillNames(
                                    tool.functionName,
                                )}
                                onUpdate={updateTool}
                            />
                        );
                    })}
                    {availableTools.length === 0 && (
                        <Grid item>
                            <Typography variant="body2" color="text.secondary">
                                No tools available.
                            </Typography>
                        </Grid>
                    )}
                </Grid>
            </Paper>
        </Grid>
    );
}

interface ToolRowProps {
    tool: ToolDefinition;
    config: KboToolConfiguration;
    requiredBySkills: string[];
    onUpdate: (
        functionName: string,
        patch: Partial<KboToolConfiguration>,
    ) => void;
}

const ToolRow = React.memo(function ToolRow({
    tool,
    config,
    requiredBySkills,
    onUpdate,
}: ToolRowProps) {
    const [expanded, setExpanded] = useState(false);
    const isRequiredBySkill = requiredBySkills.length > 0;
    const checkboxDisabled = isRequiredBySkill;
    const effectiveConfig = useMemo(
        () =>
            isRequiredBySkill && !config.enabled
                ? { ...config, enabled: true }
                : config,
        [config, isRequiredBySkill],
    );
    const hasConfigurableProperties =
        tool.configurableProperties && tool.configurableProperties.length > 0;
    const excludedSet = useMemo(
        () => new Set(effectiveConfig.excludedProperties ?? []),
        [effectiveConfig.excludedProperties],
    );
    const skillRequirementTooltip = isRequiredBySkill
        ? `This tool has been added due to the following skills being loaded: ${requiredBySkills.join(
              ", ",
          )}`
        : "";

    const toggleProperty = useCallback(
        (propertyName: string, included: boolean) => {
            const currentExcluded = new Set(
                effectiveConfig.excludedProperties ?? [],
            );
            if (included) {
                currentExcluded.delete(propertyName);
            } else {
                currentExcluded.add(propertyName);
            }
            const newExcluded =
                currentExcluded.size > 0 ? Array.from(currentExcluded) : null;
            onUpdate(tool.functionName, { excludedProperties: newExcluded });
        },
        [effectiveConfig.excludedProperties, onUpdate, tool.functionName],
    );

    return (
        <Grid
            container
            item
            direction="column"
            sx={{
                paddingInline: 1,
                paddingBlock: 0.5,
                borderRadius: 1,
                "&:hover": {
                    backgroundColor: "action.hover",
                },
            }}
        >
            <Grid container item alignItems="center" columnGap={2}>
                <Grid item xs={3} sx={{ minWidth: 0 }}>
                    <FormControlLabel
                        sx={{
                            display: "flex",
                            width: "100%",
                            minWidth: 0,
                            mr: 0,
                        }}
                        control={
                            <Tooltip title={skillRequirementTooltip}>
                                <span>
                                    <Checkbox
                                        size="small"
                                        checked={effectiveConfig.enabled}
                                        disabled={checkboxDisabled}
                                        onChange={(_, checked) =>
                                            onUpdate(tool.functionName, {
                                                enabled: checked,
                                            })
                                        }
                                    />
                                </span>
                            </Tooltip>
                        }
                        label={
                            <Typography
                                variant="body2"
                                sx={{
                                    minWidth: 0,
                                    overflowWrap: "anywhere",
                                }}
                            >
                                {tool.functionName}
                                {tool.isInfrastructure && (
                                    <Typography
                                        component="span"
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ display: "block" }}
                                    >
                                        Infrastructure
                                        {tool.pluginName
                                            ? ` · ${tool.pluginName}`
                                            : ""}
                                    </Typography>
                                )}
                            </Typography>
                        }
                    />
                </Grid>
                <Grid item xs={2}>
                    <AcxMainTextField
                        id={`tool-namespace-${tool.functionName}`}
                        labelText="Namespace"
                        placeholderText={tool.namespace ?? ""}
                        value={
                            effectiveConfig.namespace ?? tool.namespace ?? ""
                        }
                        onBlur={(value) => {
                            const namespace = String(value ?? "");
                            if (
                                namespace ===
                                (effectiveConfig.namespace ??
                                    tool.namespace ??
                                    "")
                            )
                                return;
                            onUpdate(tool.functionName, {
                                namespace: namespace || null,
                            });
                        }}
                        isDisabled={!effectiveConfig.enabled}
                        fullWidth
                    />
                </Grid>
                <Grid item xs={2}>
                    <AcxMainTextField
                        id={`tool-group-${tool.functionName}`}
                        labelText="Group"
                        value={effectiveConfig.groupName ?? ""}
                        onBlur={(value) => {
                            const groupName = String(value ?? "");
                            if (groupName === (effectiveConfig.groupName ?? ""))
                                return;
                            onUpdate(tool.functionName, {
                                groupName: groupName || null,
                            });
                        }}
                        isDisabled={!effectiveConfig.enabled}
                        fullWidth
                    />
                </Grid>
                <Grid item xs={2}>
                    <RadioGroup
                        row
                        value={
                            effectiveConfig.loadingMode ??
                            tool.defaultLoadingMode ??
                            KBO_TOOL_LOADING_MODES.deferred
                        }
                        onChange={(event) =>
                            onUpdate(tool.functionName, {
                                loadingMode: event.target.value as
                                    | "deferred"
                                    | "eager",
                            })
                        }
                    >
                        <FormControlLabel
                            value={KBO_TOOL_LOADING_MODES.deferred}
                            control={<Radio size="small" />}
                            label="Deferred"
                            disabled={!effectiveConfig.enabled}
                        />
                        <FormControlLabel
                            value={KBO_TOOL_LOADING_MODES.eager}
                            control={<Radio size="small" />}
                            label="Eager"
                            disabled={!effectiveConfig.enabled}
                        />
                    </RadioGroup>
                </Grid>
                {effectiveConfig.enabled && hasConfigurableProperties && (
                    <Grid item>
                        <IconButton
                            size="small"
                            onClick={() => setExpanded((prev) => !prev)}
                            aria-label="toggle property configuration"
                        >
                            {expanded ? (
                                <ExpandLessIcon fontSize="small" />
                            ) : (
                                <ExpandMoreIcon fontSize="small" />
                            )}
                        </IconButton>
                    </Grid>
                )}
            </Grid>
            {hasConfigurableProperties && (
                <Collapse
                    in={expanded && effectiveConfig.enabled}
                    mountOnEnter
                    unmountOnExit
                >
                    <Grid
                        container
                        item
                        sx={{ pl: 5, pt: 1, pb: 1 }}
                        columnGap={1}
                        rowGap={0.5}
                    >
                        <Grid item xs={12}>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                            >
                                Schema properties (unchecked = excluded from LLM
                                schema)
                            </Typography>
                        </Grid>
                        {tool.configurableProperties!.map((prop) => (
                            <Grid item key={prop}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            size="small"
                                            checked={!excludedSet.has(prop)}
                                            onChange={(_, checked) =>
                                                toggleProperty(prop, checked)
                                            }
                                        />
                                    }
                                    label={
                                        <Typography variant="caption">
                                            {prop}
                                        </Typography>
                                    }
                                />
                            </Grid>
                        ))}
                    </Grid>
                </Collapse>
            )}
        </Grid>
    );
});
